import type { ForcedSubject, MongoAbility, RawRuleOf } from '@casl/ability'
import type { PackRule } from '@casl/ability/extra'

/**
 * What an access check asks for. Mirrors studio-api's `Permission`
 * (`src/modules/v2/access/permissions.ts`): the values are a contract with the
 * rules `GET /v2/access/me` returns, so renaming one breaks every check on it.
 * Screens ask for a permission, never a role.
 */
export enum Permission {
  OrganizationView = 'organization.view',
  OrganizationSettingsManage = 'organization.settings.manage',
  OrganizationMembersManage = 'organization.members.manage',
  OrganizationOwnersManage = 'organization.owners.manage',
  OrganizationWorkspacesManage = 'organization.workspaces.manage',
  OrganizationBillingManage = 'organization.billing.manage',

  WorkspaceView = 'workspace.view',
  ContentView = 'content.view',
  SubmissionsView = 'submissions.view',
  GradingRun = 'grading.run',
  ContentManage = 'content.manage',
  SubmissionsExport = 'submissions.export',
  WorkspaceSettingsManage = 'workspace.settings.manage',

  PlatformOrganizationsManage = 'platform.organizations.manage',
  PlatformUsersManage = 'platform.users.manage',
  PlatformSettingsManage = 'platform.settings.manage',
  PlatformBillingManage = 'platform.billing.manage',
}

/** Where a permission applies, which decides what a check is asked about. */
export enum PermissionLevel {
  Platform = 'platform',
  Organization = 'organization',
  Workspace = 'workspace',
}

/** The CASL subject types the API's rules name. */
export enum AccessSubjectType {
  Platform = 'Platform',
  Organization = 'Organization',
  Workspace = 'Workspace',
}

type AccessSubject =
  | AccessSubjectType
  | 'all'
  | ForcedSubject<AccessSubjectType.Platform>
  | (ForcedSubject<AccessSubjectType.Organization> & { id: string })
  | (ForcedSubject<AccessSubjectType.Workspace> & {
      id: string
      organizationId: string
    })

/**
 * The signed-in person's CASL rules. `manage` and `all` are CASL's own "any
 * action" and "any subject", which only a Superadmin's rules use.
 */
export type AccessAbility = MongoAbility<[Permission | 'manage', AccessSubject]>

/** One of the person's own workspace rows, as `GET /v2/access/me` lists it. */
export interface AccessMeWorkspaceRole {
  workspaceId: string
  organizationId: string
  role: 'admin' | 'standard' | 'viewer'
}

/**
 * `GET /v2/access/me`. What to show is decided from `rules` alone; the three
 * lists only name the person's own roles, and leave out Superadmin rows.
 */
export interface AccessMe {
  /** Organisations where the person is an Owner. */
  ownerOf: string[]
  /** Organisations where the person is an Org admin. */
  orgAdminOf: string[]
  /** The person's own workspace rows, not the workspaces an Owner reaches. */
  workspaceRoles: AccessMeWorkspaceRole[]
  /** CASL rules packed with `packRules`; `unpackAccessRules` reverses it. */
  rules: PackRule<RawRuleOf<AccessAbility>>[]
}

/**
 * What a check is about: the organisation, and for a workspace permission the
 * workspace too. Platform permissions need neither.
 */
export interface AccessScope {
  organizationId?: string | null
  workspaceId?: string | null
}

/** Someone's organisation role after an Owner or organisation role route. */
export interface OrganizationRoleChange {
  userId: string
  organizationId: string
  role: string
  updatedAt: string
}

/** Someone's workspace role after the workspace role route. */
export interface WorkspaceRoleChange {
  userId: string
  workspaceId: string
  role: string
  updatedAt: string
}
